import { useState, useEffect } from 'react';
import { MdLock, MdInfoOutline } from 'react-icons/md';
import Card from '../common/Card';
import DualDatePicker from '../common/DualDatePicker';
import useAuth from '../../hooks/useAuth';
import { getSettings, updateOpeningBalances } from '../../services/settingsService';

// Only these three are ever shown. "Unclassified" stays in the data model (defaulting to 0) but
// is a technical artifact of the payment-method mapping — never asked of the owner.
const VISIBLE_ACCOUNTS = [
  ['cash', 'Cash in drawer'],
  ['bank', 'In bank'],
  ['ewallet', 'In eSewa / Khalti'],
];

const toIso = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

// "Starting Money" — the dated seed the Ledger anchors balances to. Primary-Admin only; staff see
// it read-only. Saves through its own audit-logged endpoint (not the Fees form). No math here —
// just a plain-English capture of "how much did you have when you started".
export default function OpeningBalancesCard() {
  const { user } = useAuth();
  const isPrimary = user?.adminType === 'primary' || user?.isPrimaryAdmin === true;

  // `unclassified` is held but never rendered — preserved across saves so we never silently zero
  // a value the owner can't see.
  const [amounts, setAmounts] = useState({ cash: '', bank: '', ewallet: '', unclassified: 0 });
  const [asOf, setAsOf] = useState('');
  const [skip, setSkip] = useState(false); // "I don't know — just start from zero"
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    getSettings()
      .then(res => {
        if (!alive) return;
        const s = res.data?.settings || {};
        const ob = s.openingBalances || {};
        const loaded = {
          cash: ob.cash ?? 0, bank: ob.bank ?? 0, ewallet: ob.ewallet ?? 0, unclassified: ob.unclassified ?? 0,
        };
        const iso = toIso(s.openingBalanceAsOf);
        setAmounts(loaded);
        setAsOf(iso);
        // Nothing configured yet (no date and no amounts) → default to the start-from-zero state.
        const unconfigured = !iso && !loaded.cash && !loaded.bank && !loaded.ewallet && !loaded.unclassified;
        setSkip(unconfigured);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const setAmt = (k, val) => setAmounts(a => ({ ...a, [k]: val }));

  const toggleSkip = (checked) => {
    setSkip(checked);
    if (checked) { setAmounts(a => ({ cash: '', bank: '', ewallet: '', unclassified: a.unclassified })); setAsOf(''); }
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const payload = skip
        ? { openingBalances: { cash: 0, bank: 0, ewallet: 0, unclassified: amounts.unclassified || 0 }, openingBalanceAsOf: null }
        : {
            openingBalances: {
              cash: Number(amounts.cash) || 0, bank: Number(amounts.bank) || 0,
              ewallet: Number(amounts.ewallet) || 0, unclassified: amounts.unclassified || 0,
            },
            openingBalanceAsOf: asOf || null,
          };
      await updateOpeningBalances(payload);
      setMsg({ type: 'ok', text: 'Saved.' });
    } catch (err) {
      setMsg({ type: 'err', text: err?.response?.data?.message || 'Could not save.' });
    } finally {
      setSaving(false);
    }
  };

  const disabled = !isPrimary || saving || skip;
  const inputCls = 'w-full min-h-[44px] rounded-xl border border-divider bg-card text-secondary text-sm px-3 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60';

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-1">
        <h3 className="text-sm font-semibold text-secondary">Starting Money</h3>
        {!isPrimary && (
          <span className="inline-flex items-center gap-1 text-xs text-muted flex-shrink-0">
            <MdLock className="text-sm" /> Primary Admin only
          </span>
        )}
      </div>
      <p className="text-xs text-subtle mb-4">How much money did you have when you started using TrainAxis?</p>

      {loading ? (
        <div className="h-24 bg-nav-hover rounded-xl animate-pulse" />
      ) : !isPrimary ? (
        // Read-only for staff admins — same plain labels.
        <div className="space-y-2 text-sm">
          {asOf ? (
            <>
              {VISIBLE_ACCOUNTS.map(([k, label]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-muted">{label}</span>
                  <span className="text-secondary">NPR {Number(amounts[k] || 0).toLocaleString('en-US')}</span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-1 border-t border-divider">
                <span className="text-muted">Starting from</span>
                <span className="text-secondary">{asOf}</span>
              </div>
            </>
          ) : (
            <p className="text-muted">Not set — balances start from zero.</p>
          )}
        </div>
      ) : (
        <>
          <div className={skip ? 'opacity-50 pointer-events-none' : ''}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {VISIBLE_ACCOUNTS.map(([k, label]) => (
                <div key={k}>
                  <label className="block text-xs font-medium text-muted mb-1">{label} (NPR)</label>
                  <input
                    type="number" min="0" step="0.01" inputMode="decimal"
                    value={amounts[k]} onChange={e => setAmt(k, e.target.value)}
                    disabled={disabled} className={inputCls}
                  />
                </div>
              ))}
            </div>

            <div className="mt-4">
              <label className="block text-xs font-medium text-muted mb-1">Starting from</label>
              <DualDatePicker value={asOf} onChange={setAsOf} max={toIso(new Date())} />
              <p className="text-xs text-subtle mt-1">The day you counted this money.</p>
            </div>
          </div>

          <label className="flex items-center gap-2 mt-4 cursor-pointer">
            <input type="checkbox" checked={skip} onChange={e => toggleSkip(e.target.checked)} className="w-4 h-4 accent-primary rounded" />
            <span className="text-sm text-secondary">I don&apos;t know — just start from zero</span>
          </label>

          {msg && (
            <div className={`mt-4 flex items-center gap-1.5 text-xs ${msg.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>
              <MdInfoOutline className="text-sm flex-shrink-0" /> {msg.text}
            </div>
          )}

          <div className="flex justify-end mt-4">
            <button
              type="button" onClick={save} disabled={saving}
              className="min-h-[40px] px-5 rounded-full bg-primary text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
    </Card>
  );
}
